/**
 * The dashboard's lead element: today's calls split by what actually happened.
 * A proportional bar answers "did the phone do its job today" faster than four
 * separate counters, which is the first question an owner asks.
 */
export function CallVolumeBar({
  ordered,
  answered,
  transferred,
  missed
}: {
  ordered: number;
  answered: number;
  transferred: number;
  missed: number;
}) {
  const segments = [
    { label: 'became orders', value: ordered, className: 'bg-jade' },
    { label: 'answered', value: answered, className: 'bg-jade/40' },
    { label: 'passed to staff', value: transferred, className: 'bg-amber' },
    { label: 'hung up', value: missed, className: 'bg-rust' }
  ];

  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  return (
    <div className="px-4 py-5">
      <div className="flex items-baseline gap-3">
        <span className="tabular text-hero text-ink">{total}</span>
        <span className="text-small text-slate">
          {total === 1 ? 'call today' : 'calls today'}
        </span>
      </div>

      {total === 0 ? (
        <p className="mt-4 text-small text-slate">
          Nothing has come in yet today. Calls appear here as they happen.
        </p>
      ) : (
        <>
          <div
            className="mt-4 flex h-2.5 w-full overflow-hidden rounded-sm bg-rule"
            role="img"
            aria-label={segments
              .map((segment) => `${segment.value} ${segment.label}`)
              .join(', ')}
          >
            {segments.map((segment) =>
              segment.value === 0 ? null : (
                <div
                  key={segment.label}
                  className={segment.className}
                  style={{ width: `${(segment.value / total) * 100}%` }}
                />
              )
            )}
          </div>

          <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
            {segments.map((segment) => (
              <li key={segment.label} className="flex items-center gap-2 text-small">
                <span className={`h-2.5 w-2.5 rounded-sm ${segment.className}`} />
                <span className="tabular text-ink">{segment.value}</span>
                <span className="text-slate">{segment.label}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
